import { useCallback, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { Cycle } from '../../../../store/models/cycles';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import ROUTE from '../../../../constants/route-constants';
import { useAbilityContext } from '../../../../components/Can/Can';
import { Action } from '../../../../enums/action';
import { EXTENDED_INTERVAL_UPDATE_DATA, PAGE_TITLE } from '../../../../constants/common';
import { IActionsDropdown, IActionsTag } from '../../../../types/tableDropdown';
import IconEditOutlined from '../../../../components/Icons/IconEditOutlined/IconEditOutlined';
import { ElementSize } from '../../../../types/element-size';
import IconBin from '../../../../components/Icons/IconBin/IconBin';
import IconCopy from '../../../../components/Icons/IconCopy/IconCopy';
import IconInputOne from '../../../../components/Icons/IconInputOne/IconInputOne';
import { filterCyclesBySearchQuery } from '../../utils/filterCyclesBySearchQuery';
import { usePolling } from '../../../../hooks/usePolling';
import { useTranslation } from 'react-i18next';
import { joinActionGroups } from '../../../../utils/actionsDropdown';

export interface ICompletedCyclesInteractor {
  cycles: Cycle[];

  isLoading: boolean;

  cycleActions: IActionsDropdown[];

  cycleIdSettings: string;
  cycleNameSettings: string;
  openSelectedCycleSettings: (cycle: Cycle) => void;
  closeSelectedCycleSettings: () => void;

  cycleIdDelete: string;
  closeSelectedCycleDelete: () => void;
  onAcceptSelectedCycleDelete: () => void;

  cycleIdDuplicate: string;
  cycleNameDuplicate: string;
  closeSelectedCycleDuplicate: () => void;
  onCycleDuplicated: () => void;
}

export const useCompletedCyclesInteractor = (): ICompletedCyclesInteractor => {
  const [isLoading, setIsLoading] = useState(false);
  const [cycleIdSettings, setCycleIdSettings] = useState<string>('');
  const [cycleNameSettings, setCycleNameSettings] = useState<string>('');
  const [cycleIdDelete, setCycleIdDelete] = useState<string>('');
  const [cycleIdDuplicate, setCycleIdDuplicate] = useState<string>('');
  const [cycleNameDuplicate, setCycleNameDuplicate] = useState<string>('');

  const { t } = useTranslation();
  const { clientId, contractId } = useParams();
  const navigate = useNavigate();

  const {
    cycles: { getCompletedCycles, deleteCycle, getCyclesCount },
  } = useAppDispatch();

  const { completedCycles, searchCycleQuery } = useAppSelector((state) => state.cycles);

  const cycles = useMemo(() => {
    if (!searchCycleQuery) {
      return completedCycles;
    }

    return filterCyclesBySearchQuery(completedCycles, searchCycleQuery);
  }, [completedCycles, searchCycleQuery]);

  const ability = useAbilityContext();

  const [canDeleteCycle, canUpdateCycle, canReadCycle] = useMemo(() => {
    return [
      ability.can(Action.Delete, PAGE_TITLE.EDIT_CYCLE),
      ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE),
      ability.can(Action.Read, PAGE_TITLE.EDIT_CYCLE),
    ];
  }, [ability]);

  const isChangeLoading = useRef(true);

  const fetchDataHandler = async (contractId: string) => {
    try {
      if (!contractId) {
        return;
      }

      if (isChangeLoading.current) {
        setIsLoading(true);
      }

      await getCompletedCycles(contractId);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      isChangeLoading.current = false;
    }
  };

  //cycle settings
  const openSelectedCycleSettings = useCallback((cycle: Cycle) => {
    setCycleIdSettings(cycle.id);
    setCycleNameSettings(cycle.name);
  }, []);

  const closeSelectedCycleSettings = useCallback(() => {
    setCycleIdSettings('');
    setCycleNameSettings('');
  }, []);

  //cycle delete
  const openSelectedCycleDelete = useCallback((cycleId: string) => {
    setCycleIdDelete(cycleId);
  }, []);

  const closeSelectedCycleDelete = useCallback(() => {
    setCycleIdDelete('');
  }, []);

  const dataRefetch = async (contractId: string) => {
    await Promise.all([getCyclesCount(contractId), fetchDataHandler(contractId)]);
  };

  const onAcceptSelectedCycleDelete = useCallback(async () => {
    closeSelectedCycleDelete();

    await deleteCycle(cycleIdDelete);

    await dataRefetch(contractId);
  }, [contractId, cycleIdDelete, closeSelectedCycleDelete]);

  //multi-duplicate cycle
  const openSelectedCycleDuplicate = useCallback((cycleId: string, cycleName: string) => {
    setCycleIdDuplicate(cycleId);
    setCycleNameDuplicate(cycleName);
  }, []);

  const closeSelectedCycleDuplicate = useCallback(() => {
    setCycleIdDuplicate('');
    setCycleNameDuplicate('');
  }, []);

  const onCycleDuplicated = useCallback(() => {
    dataRefetch(contractId);
  }, [contractId]);

  const editCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />,
      name: t('commonText.edit'),
      onClick: (cycleId: string) => {
        const path = generatePath(ROUTE.EDIT_CYCLE_WITHOUT_SCREEN, { clientId, contractId, id: cycleId });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
    };
  }, [navigate, clientId, contractId]);

  const deleteCycleFromScreenActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconBin fill="#000" size={ElementSize.XXS} />,
      name: t('commonText.delete'),
      onClick: (cycleId: string) => {
        openSelectedCycleDelete(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleDelete]);

  const openCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconInputOne size={ElementSize.XXS} />,
      name: t('commonText.openChart'),
      onClick: (cycleId: string) => {
        const path = generatePath(ROUTE.CYCLE_WITHOUT_SCREEN, {
          clientId,
          contractId,
          id: cycleId,
        });
        window.open(path, '_blank');
      },
      loading: false,
      tag: IActionsTag.Link,
    };
  }, [clientId, contractId]);

  const duplicateCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => <IconCopy fill="#000" size={ElementSize.XXS} />,
      name: 'Multi-duplicate',
      onClick: (cycleId: string, cycleData: Cycle) => {
        openSelectedCycleDuplicate(cycleId, cycleData.name);
      },
      loading: false,
    };
  }, [openSelectedCycleDuplicate]);

  const cycleActions: IActionsDropdown[] = useMemo(() => {
    const viewGroup: IActionsDropdown[] = canReadCycle ? [openCycleActionBtn] : [];

    const editGroup: IActionsDropdown[] = canUpdateCycle ? [editCycleActionBtn, duplicateCycleActionBtn] : [];

    const deleteGroup: IActionsDropdown[] = canDeleteCycle ? [deleteCycleFromScreenActionBtn] : [];

    return joinActionGroups(viewGroup, editGroup, deleteGroup);
  }, [
    canUpdateCycle,
    canDeleteCycle,
    canReadCycle,
    editCycleActionBtn,
    deleteCycleFromScreenActionBtn,
    openCycleActionBtn,
    duplicateCycleActionBtn,
  ]);

  usePolling(() => fetchDataHandler(contractId), EXTENDED_INTERVAL_UPDATE_DATA, [contractId]);

  return {
    cycles,

    isLoading,
    cycleActions,

    //cycle settings
    cycleIdSettings,
    cycleNameSettings,
    openSelectedCycleSettings,
    closeSelectedCycleSettings,

    //delete cycle from screen
    cycleIdDelete,
    closeSelectedCycleDelete,
    onAcceptSelectedCycleDelete,

    //multi-duplicate cycle
    cycleIdDuplicate,
    cycleNameDuplicate,
    closeSelectedCycleDuplicate,
    onCycleDuplicated,
  };
};
